import {Injectable} from '@angular/core';
import {DbResponse} from "../shared/interceptors/db-response";
import {GlobalEventDispatcherService} from "../shared/services/global-event-dispatcher/global-event-dispatcher.service";
import {GlobalEvent} from "../shared/services/global-event-dispatcher/global-event";
import {RequesterService} from "../shared/services/requester/requester.service";
import {BehaviorSubject} from "rxjs";
import {Router} from "@angular/router";
import {Config} from "../config/config";
import {DbStoreState} from '../store/reducers';
import {Store} from '@ngrx/store';
import * as fromStore from '../store';
import {CookieService} from "ngx-cookie";
import {GoogleAnalyticsService} from "../shared/services/google-analytics/google-analytics.service";
import {GoogleAnalytics4Service} from "../shared/services/google-analytics-4/google-analytics-4.service";
import {UserSubscription} from "../user/user-subscription";
import {UserService} from "../user/user.service";
import {TypeFormQuestions} from "../add-website/typeFormAnswers";
import {TypeUtils} from "../shared/utils/type-utils";
import {filter, first, map} from "rxjs/operators";
import {BuilderSectionsInteractionService} from "../builder-v2/builder-sections-interaction.service";
import {BuilderHistoryService} from "../builder-v2/builder-history.service";
import {ResellerCidService} from "../shared/services/reseller-cid/reseller-cid.service";


@Injectable()
export class AuthService {

  private _isTokenRefreshing: boolean;
  private currentWorkspaceId: any;
  private ownerWorkspaceId: any;

  constructor(
    private _router: Router,
    private _store: Store<DbStoreState>,
    private _globalEventDispatcher: GlobalEventDispatcherService,
    private _cookieService: CookieService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _googleAnalytics4Service: GoogleAnalytics4Service,
    private _userService: UserService,
    private _requester: RequesterService,
    private _builderSectionsInteractionsService: BuilderSectionsInteractionService,
    private _builderHistoryService: BuilderHistoryService,
    private _resellerCidService: ResellerCidService
  ) {
    this._isTokenRefreshing = false;
    this._store.select(fromStore.getWorkspaceIdEntities).subscribe( workspaceIdEntities => {
      this.ownerWorkspaceId = workspaceIdEntities.ownerWorkspaceId;
      this.currentWorkspaceId = workspaceIdEntities.currentWorkspaceId || this.ownerWorkspaceId;
    });

    _globalEventDispatcher.addEventListener(GlobalEvent.sessionTimeout, this.onSessionTimeout, this);
  }

  private onSessionTimeout(): void {
    this.refreshToken();
  }

  public refreshToken(): void {
    if (this._isTokenRefreshing) {
      return;
    }

    this._isTokenRefreshing = true;

    const tokens = {
      refresh_token : localStorage.getItem('dbUserRefreshToken'),
      access_token : localStorage.getItem('dbUserAccessToken')
    };
    localStorage.removeItem('dbUserAccessToken');
    localStorage.removeItem('dbUserRefreshToken');

    this._requester.core
      .post('refresh', null, tokens)
      .pipe(
        filter((response: DbResponse): boolean => !!response),
        first())
      .subscribe((response: DbResponse): void => {
        this._isTokenRefreshing = false;

        if (response.success) {

          localStorage.setItem('dbUserAccessToken', response.data.token);
          localStorage.setItem('dbUserRefreshToken', response.data.refresh_token);

          this._globalEventDispatcher.dispatchEvent(GlobalEvent.sessionRefreshed);
        }
        else {
          this._globalEventDispatcher.dispatchEvent(GlobalEvent.sessionRefreshFailed, response.response);
          this.logout(null);
        }
      });
  }

  public recaptchaEnv() {
    const data = {
      service_key: Config.serviceKey,
    };
    return this._requester.core
      .get(`recaptcha_mode`, data, true);
  }

  public login(credentials: any, silent: boolean = false, recaptchaToken = null, recovery = false, authKeyFromGoogle = null): BehaviorSubject<DbResponse> {
    if (authKeyFromGoogle) {
      credentials = Object.assign({}, credentials, {
        auth_key: authKeyFromGoogle,
      });
    }
    if (recaptchaToken) {
      credentials = Object.assign({}, credentials, {
        google_recaptcha: recaptchaToken,
      });
    }
    if (credentials.login2faCode) {
      if (recovery) {
        credentials = Object.assign({}, credentials, {
          '2fa_recovery_token': credentials.login2faCode,
        });
      }
      else {
        credentials = Object.assign({}, credentials, {
          '2fa_token': credentials.login2faCode,
        });
      }
    }
    const resellerCid = this._resellerCidService.get();
    const tenWebGaUser = this._cookieService.get("tenweb_ga_user") && this._cookieService.get("tenweb_ga_user").indexOf('Tenweb_') === 0 && this._cookieService.get("tenweb_ga_user");
    credentials = Object.assign({}, credentials, {
      ga_client_cookie: resellerCid || tenWebGaUser || '',
      isBlocked: resellerCid ? 0 : (tenWebGaUser ? 1 : 3),
    });
    return <BehaviorSubject<DbResponse>>(this._requester.core
      .patch('login', null, credentials)
      .pipe(map((response: DbResponse): DbResponse => {
        if (response && response.success) {
          localStorage.setItem('dbUserRefreshToken', response.data.refresh_token);
          localStorage.setItem('dbUserAccessToken', response.data.token);
          this._store.dispatch(new fromStore.SetWorkspaceIdEntities(
            {
              ownerWorkspaceId: response.data.workspace_id,
              currentWorkspaceId: response.data.current_workspace_id || response.data.workspace_id,
              currentWorkspaceRoleId: response.data.role_id,
              globalEventDispatcherService: this._globalEventDispatcher
            }
          ));

          if (!silent && response.data && !response.data['requires_2fa']) {
            this._globalEventDispatcher.dispatchEvent(GlobalEvent.userLogIn);
            this._store.dispatch(new fromStore.UserLogIn);
          }
        }
        return response;
      })));
  }

  public proceedWithGoogle(googleToken, subscriptionId, from_plugin = null, product_id = null, client_id = null, affiliateId = null, sign_up_for = null, twoFaToken = null) {
    let data: any = {'google_token': googleToken};
    if (subscriptionId) {
      data.subscription_id = subscriptionId;
    }

    if (client_id) {
      data = Object.assign({}, data, {
        ghost_client_id: client_id,
      });
    }
    if (twoFaToken) {
      data = Object.assign({}, data, {
        '2fa_token': twoFaToken,
      });
    }
    let isBlocked: number;
    const resellerCid = this._resellerCidService.get();
    const tenWebGaUser = this._cookieService.get("tenweb_ga_user") && this._cookieService.get("tenweb_ga_user").indexOf('Tenweb_') === 0 && this._cookieService.get("tenweb_ga_user");
    const _cid = resellerCid || tenWebGaUser || '';
    isBlocked = resellerCid ? 0 : (tenWebGaUser ? 1 : 3);
    data = Object.assign({}, data, {
      ga_client_cookie: _cid,
      isBlocked: isBlocked,
    });
    if (from_plugin) {
      data = Object.assign({}, data, {
        from_plugin: '1',
      });
    }
    if (product_id) {
      data = Object.assign({}, data, {
        product_id: product_id,
      });
    }

    if (affiliateId) {
      data = Object.assign({}, data, {
        affiliate_id: affiliateId,
      });
    }

    if (sign_up_for) {
      data = Object.assign({}, data, {
        sign_up_for: sign_up_for,
      });
    }
    return <BehaviorSubject<DbResponse>>(this._requester.core
      .post('agency/google/login', null, data)
      .pipe(map((response: DbResponse): DbResponse => {
        if (response && response.success &&  response.data && response.data.dashboard_token) {
          localStorage.setItem('dbUserRefreshToken', response.data.dashboard_token.refresh_token);
          localStorage.setItem('dbUserAccessToken', response.data.dashboard_token.access_token);
          if (response.data && response.data.tenweb_cookie) {
            this._cookieService.put('FPID', response.data.tenweb_cookie, {path: '/', domain: '10web.io'});
          }
          this._store.dispatch(new fromStore.SetWorkspaceIdEntities(
            {
              ownerWorkspaceId: response.data.workspace_id,
              currentWorkspaceId: response.data.current_workspace_id || response.data.workspace_id,
              currentWorkspaceRoleId: response.data.role_id,
              globalEventDispatcherService: this._globalEventDispatcher
            }
          ));

        }
        return response;
      })));
  }

  // TODO: move can logout and clear on logout functional to appropriate services
  public canLogout(): boolean {
    const canLogout: boolean = true;

    return canLogout;
  }

  public logout(redirectTo: any = null, userId?: string, subscription?: UserSubscription, doNotRedirect: boolean = false): void {
    this._googleAnalytics4Service.clearClientId();
    this._requester.clearCache();
    localStorage.removeItem('dbUserAccessToken');
    localStorage.removeItem('dbUserRefreshToken');
    localStorage.removeItem('dbCurrentWorkspaceId');
    localStorage.removeItem('dbOwnWorkspaceId');
    localStorage.removeItem('dbCurrentWorkspaceRoleId');
    localStorage.removeItem('dbFirebaseToken');
    localStorage.removeItem('dbUserName');
    localStorage.removeItem('dbCheckAvailability');
    localStorage.removeItem('chosen-step');
    localStorage.removeItem('onboardingFlow');
    localStorage.removeItem('dbDeveloper');
    localStorage.removeItem('dbPreviousWorkspaceRoleId');
    localStorage.removeItem('dbPreviousWorkspaceId');

    if (localStorage.getItem('refLastMonth')) {
      localStorage.removeItem('refLastMonth');
    }
    if (userId && localStorage.getItem("dbManualMigrateSubDomain" + userId)) {
      localStorage.removeItem("dbManualMigrateSubDomain" + userId);
    }
    if (userId && localStorage.getItem("onHoverEventSent" + userId)) {
      localStorage.removeItem("onHoverEventSent" + userId);
    }
    if (userId && localStorage.getItem("dbManualMigrateRegion" + userId)) {
      localStorage.removeItem("dbManualMigrateRegion" + userId);
    }
    if (userId && localStorage.getItem("db-hide-proactive-greeting" + userId)) {
      localStorage.removeItem("db-hide-proactive-greeting" + userId);
    }
    if (userId && localStorage.getItem("showConnectBanner" + userId)) {
      localStorage.removeItem("showConnectBanner" + userId);
    }
    if (localStorage.getItem("dbScoreTooltipShown")) {
      localStorage.removeItem("dbScoreTooltipShown");
    }
    if (localStorage.getItem("db-hide-proactive-greeting")) {
      localStorage.removeItem("db-hide-proactive-greeting");
    }
    if (localStorage.getItem("dbFrom1MFPopupPaypal")) {
      localStorage.removeItem("dbFrom1MFPopupPaypal");
    }
    if (localStorage.getItem("builder_history")) {
      localStorage.removeItem("builder_history");
    }
    if (localStorage.getItem("builderType")) {
      localStorage.removeItem("builderType");
    }
    this._builderHistoryService.resetSubjects();
    this._builderSectionsInteractionsService.resetSubjects();
    this._cookieService.remove("db-user-id", {path: '/', domain: '10web.io'});
    this._cookieService.remove("FPID", {path: '/', domain: '10web.io'});
    this._cookieService.remove("db-original-user-id", {path: '/', domain: '10web.io'});
    this._cookieService.remove("db-user-subscr-id", {path: '/', domain: '10web.io'});
    this._cookieService.remove("db-user-subscr-type", {path: '/', domain: '10web.io'});
    this._cookieService.remove("db-user-subscr-category", {path: '/', domain: '10web.io'});

    this._store.dispatch(new fromStore.UserLogOut);
    this._store.dispatch(new fromStore.ResetWorkspaceState);
    this._store.dispatch(new fromStore.ResetUserPayments);
    this._store.dispatch(new fromStore.ResetPurchasedDomains);
    this._globalEventDispatcher.dispatchEvent(GlobalEvent.userLogOut);
    if (!doNotRedirect) {
      if (redirectTo) {
        window.location.assign(redirectTo);
      }
      else {
        this._router.navigateByUrl('/login');
      }
    }
  }

  public clearAllLocalStorage(): any {
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        localStorage.removeItem(key);
      }
    }
  }

  public logoutFromProfile(redirectTo: any = null, userId?: string, subscription?: UserSubscription, doNotRedirect: boolean = false): void {
    this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/logout`, null, null, true)
      .pipe(filter((response: DbResponse): boolean => !!response),
        first())
      .subscribe((response: DbResponse): void => {
        if (response && response.success) {
          this._googleAnalytics4Service.clearClientId();
          this._requester.clearCache();
          this.clearAllLocalStorage();
          this._cookieService.remove("db-user-id", {path: '/', domain: '10web.io'});
          this._cookieService.remove("db-original-user-id", {path: '/', domain: '10web.io'});
          this._cookieService.remove("db-user-subscr-id", {path: '/', domain: '10web.io'});
          this._cookieService.remove("db-user-subscr-type", {path: '/', domain: '10web.io'});
          this._cookieService.remove("db-user-subscr-category", {path: '/', domain: '10web.io'});
          this._cookieService.remove('db-limited-offer-expiration-date', {path: '/', domain: '10web.io'});

          this._store.dispatch(new fromStore.UserLogOut);
          this._store.dispatch(new fromStore.ResetWorkspaceState);
          this._store.dispatch(new fromStore.ResetUserPayments);
          this._store.dispatch(new fromStore.ResetPurchasedDomains);
          this._globalEventDispatcher.dispatchEvent(GlobalEvent.userLogOut);
          if (!doNotRedirect) {
            if (redirectTo) {
              window.location.assign(redirectTo);
            }
            else {
              this._router.navigateByUrl('/login');
            }
          }
        }
        return null;
      });
  }


  public checkPaypalRecurringPayment() {
    return this._requester.core
      .get(`workspaces/${this.currentWorkspaceId}/check/paypal-recurring-initial-payment`, null, true);
  }

  public checkUserExists(email) {
    const checkUserData: any = {
      email: email,
    };

    return this._requester.core
      .post(`checkout/check-unique-email`, null, checkUserData);
  }

  public sendHubspotData(email) {
    const hubspotData: any = {
      email: email,
    };

    return this._requester.core
      .post(`hubspot/send-data`, null, hubspotData);
  }

  public getAiBuilderPercent() {
    return this._requester.core
      .get(`tier-data`, null, true);
  }

  public signUpViaMagicLink(email, subscriptionId) {
    const magicData: any = {
      email: email,
      return_tokens: 1,
      subscription_id: subscriptionId
    };

    return this._requester.core
      .post(`checkout/signup-via-magic-link`, null, magicData);
  }

  public verifyEmail(token, email) {
    const emailParam: any = {
      email: email
    };

    return this._requester.public
      .get(`email-verification/check/${token}`, emailParam, false);
  }

  public applyCoupon(subscriptionId, coupon, coupon_type = "") {
    let couponParam: any = {
      coupon_code: coupon.replace(/["']/g, "")
    };
    if (coupon_type) {
      couponParam = Object.assign({}, couponParam, {
        coupon_type: coupon_type,
      });
    }
    return this._requester.core
      .post(`subscriptions/${subscriptionId}/coupons`, couponParam, false);
  }

  public verifyCoupon(subscriptionId) {
    if (this.currentWorkspaceId) {
      return this._requester.core.get(`workspaces/${this.currentWorkspaceId}/subscriptions/${subscriptionId}/coupons_auth`, null, true);
    }
    return this._requester.core.get(`subscriptions/${subscriptionId}/coupons`, null, true);
  }

  public createPaypalExecuteAgreement(data: number): BehaviorSubject<DbResponse> {
    const postData: any = {
      paypal_token: data
    };
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/checkout/paypal/execute-agreement`, null, postData);
  }

  public checkout(formData: any, workspaceInvitationId = null, invitationKey = null): BehaviorSubject<DbResponse> {

    let checkoutData: any = {
      payment_type: formData.type, // agency_suite_customer/'agency_trial',
      first_name: formData.firstName,
      last_name: formData.lastName,
      username: formData.email,
      email: formData.email,
      password: formData.password,
    };

    // Add TOS consent as 1 if checked
    if (formData.terms_privacy_consent_accepted) {
      checkoutData.terms_privacy_consent_accepted = 1;
    }

    // Add provider and payment_provider_checkout_key for agency_suite_customer flows
    if (formData.type === 'agency_suite_customer') {
      if (formData.provider) {
        checkoutData.provider = formData.provider; // 'stripe_connect' or 'paypal'
      }
      if (formData.payment_provider_checkout_key) {
        checkoutData.payment_provider_checkout_key = formData.payment_provider_checkout_key;
      }
    }


    let isBlocked: number;
    const resellerCid = this._resellerCidService.get();
    const tenWebGaUser = this._cookieService.get("tenweb_ga_user") && this._cookieService.get("tenweb_ga_user").indexOf('Tenweb_') === 0 && this._cookieService.get("tenweb_ga_user");
    const _cid = resellerCid || tenWebGaUser || '';
    isBlocked = resellerCid ? 0 : (tenWebGaUser ? 1 : 3);
    checkoutData = Object.assign({}, checkoutData, {
      ga_client_cookie: _cid + `&isBlocked=${isBlocked}`
    });

    // Add invitation_key if it exists
    if (invitationKey) {
      checkoutData = Object.assign({}, checkoutData, {
        invitation_key: invitationKey,
      });
    }

    if (workspaceInvitationId) {
      checkoutData = Object.assign({}, checkoutData, {
        workspace_invitation_id: workspaceInvitationId,
      });
    }

    return <BehaviorSubject<DbResponse>>(this._requester.core
        .post(`checkout`, null, checkoutData)
        .pipe(map((response: DbResponse): DbResponse => {
          if (response && response.success) {
            if (response.data && response.data.tenweb_cookie) {
              this._cookieService.put('FPID', response.data.tenweb_cookie, {path: '/', domain: '10web.io'});
            }
          }
          return response;
        })));
  }
  public sendResetPasswordMail(email: string): BehaviorSubject<DbResponse> {

    const postData: object = {
      'email': email
    };

    return this._requester.core
      .post(`recovery`, null, postData);
  }

  public changeEmail(email: string, password: string): BehaviorSubject<DbResponse> {
    const data = {
      'email': email,
      'password': password
    };
    return this._requester.core
      .post(`workspaces/${this.ownerWorkspaceId}/user/me/edit-email`, null, data);

  }

  private mapSignupForData(data) {
    const _data = {};
    for (const [key, value] of Object.entries(data)) {
      _data[TypeFormQuestions[key]] = value;
    }
    return _data;
  }

  public updateSignUpFor(signUpFor: any, _map: boolean = false): BehaviorSubject<DbResponse> {
    const data = {
      'sign_up_for': _map ? this.mapSignupForData(signUpFor) : signUpFor
    };
    return this._requester.core
      .post(`workspaces/${this.ownerWorkspaceId}/user/me/sign-up-for/update`, null, data);
  }


  public resendVerificationEmail(): BehaviorSubject<DbResponse> {
    return this._requester.core
      .post(`workspaces/${this.ownerWorkspaceId}/user/me/user-verification/resend`, null, null);
  }



  public resetPassword(token: string, email: string, password: string, confirmPassword: string): BehaviorSubject<DbResponse> {
    const postData: object = {
      'token': token,
      'email': email,
      'password': password,
      'password_confirmation': password
    };

    return this._requester.core
      .post(`reset`, null, postData);
  }

  public confirmStripe(paymentIntetnId, action): BehaviorSubject<DbResponse> {
    const postData: object = {
      'payment_intent_id': paymentIntetnId,
      'action': action,
    };

    return this._requester.core
      .post(`checkout/stripe/confirm`, null, postData);
  }

  public deleteUserAccount(currentWorkspaceId: number, password: { password: string }) {
    return this._requester.core
      .delete(`workspaces/${currentWorkspaceId}/user/me/delete`, null, password);
  }

  public downgradeAgreement(downgrade_to: string) {
    const data = {downgrade_to};
    return this._requester.core
      .post(`workspaces/${this.ownerWorkspaceId}/user/me/downgrade-plan`, null, data);
  }

  public getCheckoutSession(subscriptionId: number, email: string, fname, lname, invitationKey?: string, workspaceInvitationId?: number, customerId?: string) {
    const data: any = {
      "customer_email": email,
      "customer_name": fname + ' ' + lname,
      "metadata": {
        "customer_type": "agency_suite_customer",
        "source": "agency_dashboard"
      }
    };

    // Add invitation data if provided
    if (invitationKey) {
      data.invitation_key = invitationKey;
    }

    if (workspaceInvitationId) {
      data.invitation_id = workspaceInvitationId;
    }

    // Add customer_id if provided to reuse existing customer
    if (customerId) {
      data.customer_id = customerId;
    }

    return this._requester.core
        .post(`agency/checkout-session/create/${subscriptionId}`, null, data);
  }

  public getUserCheckoutSession(subscriptionId: number, email: string, fname, lname, customerId?: string) {
    const data: any = {
      "metadata": {
        "customer_type": "agency_suite_customer",
        "source": "agency_dashboard"
      }
    };

    // Add customer_id if provided to reuse existing customer
    if (customerId) {
      data.customer_id = customerId;
    }

    return this._requester.core
        .post(`workspaces/${this.currentWorkspaceId}/agency/checkout-session/create/${subscriptionId}`, null, data);
  }

}
